import React, { useMemo } from "react";
import { isEmpty } from "lodash";
import { Theme, useTheme } from "@emotion/react";

import useBreakpoint from "core/hooks/useBreakpoint";
import { sort } from "core/includes/sort";
import SectionContainer from "core/Components/SectionContainer";

import Wrapper from "elements/Components/TopBanner/components/Wrapper";
import ImageCarousel from "elements/Components/TopBanner/components/ImageCarousel";
import BannerImage from "elements/Components/TopBanner/components/BannerImage";
import ButtonsContainer from "elements/Components/TopBanner/components/ButtonsContainer";
import Button from "elements/Components/TopBanner/components/Button";
import ImageCarouselContainer from "elements/Components/TopBanner/components/ImageCarouselContainer";

type Props = {
    data: TopBanner
};

const TopBanner: React.FC<Props> = ({ data }) => {
    const theme: Theme = useTheme();
    const topBannerImages = sort(data.imageList);
    const { minWidth } = useBreakpoint();
    const hasUniversalButton = !!data.buttonText || !!data.secondButtonText;

    const [topBannerHeight, buttonHeight, buttonSidePadding] = useMemo(() => {
        switch (true) {
            case minWidth >= theme.breakpoints.sizes["5xl"]:
                return [888, 54, 31];
            case minWidth >= theme.breakpoints.sizes["3xl"]:
                return [619, 54, 31];
            case minWidth >= theme.breakpoints.sizes["xxl"]:
                return [560, 42, 22];
            case minWidth >= theme.breakpoints.sizes["xl"]:
                return [489, 42, 22];
            case minWidth >= theme.breakpoints.sizes["lg"]:
                return [490, 42, 22];
            case minWidth >= theme.breakpoints.sizes["md"]:
                return [600, 42, 22];
            default:
                return [385, 42, 22];
        }
    }, [minWidth, theme.breakpoints.sizes]);

    return isEmpty(topBannerImages) ? null : (
        <SectionContainer overflowHidden>
            <Wrapper topBannerHeight={topBannerHeight}>
                <ImageCarouselContainer>
                    <ImageCarousel topBannerHeight={topBannerHeight} bannerCount={topBannerImages.length}>
                        {topBannerImages.map((item: TopBannerImage) => (
                            <BannerImage
                                key={`tb-${item.id}`}
                                topBannerHeight={topBannerHeight}
                                topBannerImage={item}
                                hasUniversalButton={hasUniversalButton}
                            />
                        ))}
                    </ImageCarousel>
                </ImageCarouselContainer>
                <ButtonsContainer>
                    <Button
                        buttonData={getPrimaryLinkButtonData(data)}
                        buttonHeight={buttonHeight}
                        buttonSidePadding={buttonSidePadding}
                    />
                    <Button
                        buttonData={getSecondaryLinkButtonData(data)}
                        buttonHeight={buttonHeight}
                        buttonSidePadding={buttonSidePadding}
                    />
                </ButtonsContainer>
            </Wrapper>
        </SectionContainer>
    );
};

const getPrimaryLinkButtonData = (data: TopBanner): LinkButton => {
    return {
        buttonTheme: data.buttonTheme,
        outerURL: data.outerURL,
        innerURL: data.innerURL,
        buttonText: data.buttonText,
        buttonColour: data.buttonColour,
        buttonTextColour: data.buttonTextColour,
        buttonHoverColour: data.buttonHoverColour,
        buttonTextHoverColour: data.buttonTextHoverColour,
    }
};

const getSecondaryLinkButtonData = (data: TopBanner): LinkButton => {
    return {
        buttonTheme: data.secondButtonTheme,
        outerURL: data.secondOuterURL,
        innerURL: data.secondInnerURL,
        buttonText: data.secondButtonText,
        buttonColour: data.secondButtonColour,
        buttonTextColour: data.secondButtonTextColour,
        buttonHoverColour: data.secondButtonHoverColour,
        buttonTextHoverColour: data.secondButtonTextHoverColour,
    }
};

export default TopBanner;
